import type { ScreenerData } from "epsm-api-types/models";
import {
	formatValueNumber,
	formatValuePercent,
} from "@/components/widgets/helpers/FormattingHelpers";
import { formatPriceToEma } from "@/components/widgets/Quote/lib/helpers/quoteValueFormatters";
import { valueColor } from "@/components/widgets/Revision/utils/RevisionTableUtils";
import { cn } from "@/lib/utils";
import { componentClassNames } from "../../../config";
import { formatPositivePercentValue } from "../../../lib/helpers/formatPositivePercentValue";
import type { DataField } from "../../../type";
import { LineHeader } from "../LineHeader";
import { QuoteRow } from "../QuoteRow";

export const PriceAction = ({ data }: { data: ScreenerData | null }) => {
	if (!data) return null;

	const upperGroup: DataField[] = [
		{
			label: "Distance to High",
			value: data?.distance_to_xd_high?.["20_D"],
			format: formatValuePercent,
		},
		{
			label: "Max DrawDown 5Y",
			value: data?.max_dd_xy?.["5_Y"],
			format: formatValuePercent,
		},
	];

	const leftGroup: DataField[] = [
		{
			label: "1 month",
			value: data?.adj_close_pct_chg?.["4_W"],
			format: formatPositivePercentValue,
			className: cn(valueColor(data?.adj_close_pct_chg?.["4_W"]), "font-bold"),
		},
		{
			label: "3 months",
			value: data?.adj_close_pct_chg?.["12_W"],
			format: formatPositivePercentValue,
			className: cn(valueColor(data?.adj_close_pct_chg?.["12_W"]), "font-bold"),
		},
		{
			label: "1 year",
			value: data?.adj_close_pct_chg?.["52_W"],
			format: formatPositivePercentValue,
			className: cn(valueColor(data?.adj_close_pct_chg?.["52_W"]), "font-bold"),
		},
		{
			label: "Year to date",
			value: data?.adj_close_pct_chg?.YTD,
			format: formatPositivePercentValue,
			className: cn(valueColor(data?.adj_close_pct_chg?.YTD), "font-bold"),
		},
	];

	const rightGroup: DataField[] = [
		{
			label: "EMA 5",
			value: data?.price_to_exponential_moving_average?.["5_d"],
			format: formatPriceToEma,
			className: cn(
				valueColor(data?.price_to_exponential_moving_average?.["5_d"], true),
				"font-bold",
			),
		},
		{
			label: "EMA 20",
			value: data?.price_to_exponential_moving_average?.["20_d"],
			format: formatPriceToEma,
			className: cn(
				valueColor(data?.price_to_exponential_moving_average?.["20_d"], true),
				"font-bold",
			),
		},
		{
			label: "EMA 50",
			value: data?.price_to_exponential_moving_average?.["50_d"],
			format: formatPriceToEma,
			className: cn(
				valueColor(data?.price_to_exponential_moving_average?.["50_d"], true),
				"font-bold",
			),
		},
		{
			label: "EMA 200",
			value: data?.price_to_exponential_moving_average?.["200_d"],
			format: formatPriceToEma,
			className: cn(
				valueColor(data?.price_to_exponential_moving_average?.["200_d"], true),
				"font-bold",
			),
		},
		{
			label: "RSI",
			value: data?.relative_strength_index,
			format: formatValueNumber,
		},
	];

	const hasVisibleData =
		upperGroup.some((f) => f.value != null) ||
		leftGroup.some((f) => f.value != null) ||
		rightGroup.some((f) => f.value != null);

	if (!hasVisibleData) return null;

	return (
		<div className={componentClassNames} data-testid="price-action">
			<LineHeader title="Price Action" />

			{upperGroup.map(({ label, value, format }) =>
				value != null ? (
					<QuoteRow key={label} left={label} right={format(value)} />
				) : null,
			)}
			<div className="flex gap-4">
				<div className="flex-1">
					{leftGroup.map(({ label, value, format, className }) =>
						value != null ? (
							<QuoteRow
								key={label}
								left={label}
								right={format(value)}
								className={className}
							/>
						) : null,
					)}
				</div>
				<div className="flex-1">
					{rightGroup.map(({ label, value, format, className }) =>
						value != null ? (
							<QuoteRow
								key={label}
								left={label}
								right={format(value)}
								className={className}
							/>
						) : null,
					)}
				</div>
			</div>
		</div>
	);
};
